import React, { useEffect, useMemo, useRef, useState } from "react";
import type { CryptoAlertData } from "@crypto-trading/common/browser";
import { canonicalExchangeCode, exchangeBrand } from "@/lib/exchange-brand";
import { ExchangeLogo } from "@/components/ui/ExchangeLogo";
import { listExchangesAvailableAtAlert } from "@/lib/live-candles/candle-symbols";

export interface AlertFilterState {
  /** Announcing exchanges to keep; empty keeps all. */
  sources: string[];
  savedOnly: boolean;
  tradableOnly: boolean;
}

export const EMPTY_ALERT_FILTERS: AlertFilterState = {
  sources: [],
  savedOnly: false,
  tradableOnly: false,
};

/** Search (symbol, token name, exchange) and filters, applied to the loaded alerts. */
export function filterAlerts(
  alerts: CryptoAlertData[],
  query: string,
  filters: AlertFilterState,
  saved: Set<string>
): CryptoAlertData[] {
  const q = query.trim().toLowerCase();
  return alerts.filter((alert) => {
    if (filters.sources.length && !filters.sources.includes(canonicalExchangeCode(alert.exchange))) {
      return false;
    }
    if (filters.savedOnly && !saved.has(alert.id)) return false;
    if (filters.tradableOnly && listExchangesAvailableAtAlert(alert).length === 0) return false;
    if (!q) return true;
    return (
      alert.currency.toLowerCase().includes(q) ||
      (alert.tokenMetadata?.name ?? "").toLowerCase().includes(q) ||
      exchangeBrand(alert.exchange).name.toLowerCase().includes(q)
    );
  });
}

export function activeFilterCount(filters: AlertFilterState): number {
  return filters.sources.length + Number(filters.savedOnly) + Number(filters.tradableOnly);
}

interface AlertsFiltersProps {
  alerts: CryptoAlertData[];
  value: AlertFilterState;
  onChange: (value: AlertFilterState) => void;
  /** icon: the square mobile button; label: "Filters" with an icon (desktop). */
  variant?: "icon" | "label";
}

/** Filters dropdown: announcing exchange, saved alerts, tradable only. */
export const AlertsFilters: React.FC<AlertsFiltersProps> = ({
  alerts,
  value,
  onChange,
  variant = "label",
}) => {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const count = activeFilterCount(value);

  const sources = useMemo(() => {
    const seen = new Set<string>(value.sources);
    alerts.forEach((a) => seen.add(canonicalExchangeCode(a.exchange)));
    return [...seen].sort((a, b) => exchangeBrand(a).name.localeCompare(exchangeBrand(b).name));
  }, [alerts, value.sources]);

  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);

  const toggleSource = (code: string) =>
    onChange({
      ...value,
      sources: value.sources.includes(code)
        ? value.sources.filter((s) => s !== code)
        : [...value.sources, code],
    });

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        aria-label="Filters"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`glass relative flex items-center justify-center gap-1 ${
          variant === "icon" ? "size-10 !rounded-xl" : "h-11 !rounded-lg px-3"
        }`}
      >
        <img src="/icons/figma/filter-lines.svg" alt="" width={20} height={20} />
        {variant === "label" && (
          <span className="px-0.5 text-sm font-semibold leading-5 text-[#d4d4d4]">Filters</span>
        )}
        {count > 0 && (
          <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-accent text-[10px] font-bold text-white">
            {count}
          </span>
        )}
      </button>

      {open && (
        <div className="glass absolute right-0 top-12 z-50 w-64 p-3 shadow-2xl backdrop-blur-xl">
          <p className="mb-2 text-xs font-medium text-[#a3a3a3]">Announced on</p>
          <div className="flex max-h-48 flex-col gap-1 overflow-y-auto">
            {sources.map((code) => (
              <label
                key={code}
                className="flex cursor-pointer items-center gap-2 rounded-lg px-1.5 py-1 text-sm text-[#d4d4d4] hover:bg-white/5"
              >
                <input
                  type="checkbox"
                  checked={value.sources.includes(code)}
                  onChange={() => toggleSource(code)}
                  className="accent-[#00c27e]"
                />
                <ExchangeLogo code={code} size={16} />
                {exchangeBrand(code).name}
              </label>
            ))}
          </div>
          <div className="my-2 h-px bg-white/10" />
          <label className="flex cursor-pointer items-center gap-2 px-1.5 py-1 text-sm text-[#d4d4d4]">
            <input
              type="checkbox"
              checked={value.tradableOnly}
              onChange={(e) => onChange({ ...value, tradableOnly: e.target.checked })}
              className="accent-[#00c27e]"
            />
            Tradable on an exchange
          </label>
          <label className="flex cursor-pointer items-center gap-2 px-1.5 py-1 text-sm text-[#d4d4d4]">
            <input
              type="checkbox"
              checked={value.savedOnly}
              onChange={(e) => onChange({ ...value, savedOnly: e.target.checked })}
              className="accent-[#00c27e]"
            />
            Bookmarked only
          </label>
          {count > 0 && (
            <button
              type="button"
              onClick={() => onChange(EMPTY_ALERT_FILTERS)}
              className="mt-2 w-full rounded-lg py-1.5 text-sm font-semibold text-[#00c27e] hover:bg-white/5"
            >
              Clear filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};
